import { FC, PropsWithChildren, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import ScopedCssBaseline from '@mui/material/ScopedCssBaseline'

import logger from './logger'

const TargetDOMElement = '#page-content-wrapper #content'

/**
 * Helper component that will inject any children into the appropriate DOM element
 * within our AngularJS app. It waits for that element to exist before attempting to
 * render the React component.
 */
function AngularInjector({ children }: PropsWithChildren) {
  const [target, setTarget] = useState<Element | null>(null)

  useEffect(() => {
    const targetNodes =
      document.querySelector(TargetDOMElement) ||
      document.getElementById('fullscreen-content')

    if (!targetNodes) {
      logger.info('Watching for AngularJS app to load')

      // mutation observer to detect when the AngularJS app has finished loading
      // and the element we want to inject into is available
      const reactTargetObserver = new MutationObserver((mutationsList) => {
        for (const mutation of mutationsList) {
          if (mutation.type === 'childList') {
            for (const node of mutation.addedNodes) {
              if (
                node instanceof HTMLElement &&
                node.nodeName === 'DIV' &&
                node.id === 'wrapper' &&
                node.querySelector('#page-content-wrapper #content')
              ) {
                // we purposefully don't disconnect our mutation observer as
                // under some conditions the AngularJS app can re-render when
                // we're on a React managed page, and without us detecting the
                // node mutation here we end up losing the React app altogether.
                // That is, the AngularJS app regenerates the DOM structure that
                // has the React app rendered into it, and in doing so removes
                // the content that we've rendered via React.
                //
                // What we have here instead is a mutation observer that is
                // always listening, and whenever it detects that the React app
                // target element has changed it will trigger a re-render of
                // the React app so that it's content is replaced.
                setTarget(
                  document.querySelector('#page-content-wrapper #content')
                )
              } else if (
                node instanceof HTMLElement &&
                node.nodeName === 'DIV' &&
                node.id === 'fullscreen-content'
              ) {
                // It's important that we don't disconnect from the observer
                // here either as this condition shares the same observer as
                // the main React app layout above.
                setTarget(node)
              }
            }
          }
        }
      })

      const bodyNode = document.querySelector('body')

      if (bodyNode) {
        reactTargetObserver.observe(bodyNode, {
          attributes: false,
          childList: true,
          subtree: true,
        })
      } else {
        logger.error(
          'Unable to find body node to wait to inject React component into'
        )
      }
    } else {
      // the element we want to inject into is already available
      logger.info('AngularJS app loaded, rendering React component')
      setTarget(targetNodes)
    }
  }, [])

  return target
    ? createPortal(<ScopedCssBaseline>{children}</ScopedCssBaseline>, target)
    : null
}

/**
 * A higher-order-component that will ensure the component being wrapped is
 * correctly injected into the AngularJS app in the appropriate position.
 * @param Component
 * @returns
 */
export default function withAngularJSCompatibility(Component: FC) {
  return function WithAngularJSCompabilty(props) {
    return (
      <AngularInjector>
        <Component {...props} />
      </AngularInjector>
    )
  }
}
